import type {
  DocumentSchema,
  SearchResponse,
  SearchResponseHit,
} from 'typesense/lib/Typesense/Documents';

/**
 * A deterministic hash function based on FNV-1a.
 * Uses bitwise operations for efficient hashing:
 * - ^= (XOR) combines the current hash with each character code
 * - << (Left shift) creates bit patterns for the mixing function
 * - >>> (Unsigned right shift) ensures a positive 32-bit integer result
 *
 * @param str - The input string to hash
 * @returns A positive integer hash value
 */
const hashString = (str: string): number => {
  let hash = 2_166_136_261; // FNV offset

  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash +=
      (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }

  return hash >>> 0; // Convert to unsigned 32-bit integer
};

/**
 * Combines and intersperses premium and free assets in a deterministic pattern that aims to:
 * 1. Always show a free asset at the start of the list
 * 2. Maintain a natural distribution ratio between premium and free assets
 * 3. Prevent more than two premium assets from appearing consecutively
 * 4. Create a consistent, pseudo-random distribution that feels natural
 *
 * The algorithm works by:
 * - Starting with 1 free asset
 * - Processing remaining assets in chunks of 4 items
 * - Using a hash-based randomization to decide placement
 * - Adjusting the premium/free ratio dynamically based on:
 *   - The desired overall ratio of premium to free assets
 *   - The number of premium assets already placed in the current chunk
 *   - A maximum of 2 consecutive premium assets
 *
 * @param options - Configuration object
 * @param options.premiumAssetSearchResults - Search results containing premium assets
 * @param options.freeAssetSearchResults - Search results containing free assets
 * @returns An array of mixed premium and free assets in the desired distribution
 *
 * @example
 * const mixed = intersperseAssets({
 *   premiumAssetSearchResults: { hits: [...] },
 *   freeAssetSearchResults: { hits: [...] }
 * });
 */
export const intersperseAssets = <T extends DocumentSchema>({
  premiumAssetSearchResults,
  freeAssetSearchResults,
}: {
  premiumAssetSearchResults: SearchResponse<T>;
  freeAssetSearchResults: SearchResponse<T>;
}): SearchResponseHit<T>[] => {
  const premiumAssets = premiumAssetSearchResults.hits;
  const freeAssets = freeAssetSearchResults.hits;
  const isFirstPage = freeAssetSearchResults.page === 1;

  if (
    !(
      premiumAssets &&
      freeAssets &&
      (premiumAssets.length || freeAssets.length)
    )
  ) {
    return [];
  }

  // Pre-calculate array size to avoid resizing
  const totalLength = Math.min(
    2 + premiumAssets.length + freeAssets.length - 2,
    premiumAssetSearchResults.found + freeAssetSearchResults.found
  );

  const result = new Array<SearchResponseHit<T>>(totalLength);

  let resultIndex = 0;
  let premiumIndex = 0;
  let freeIndex = 0;

  // Calculate the desired ratio based on total counts
  // Force the ratio to 0.5 for the first page as we want to avoid clumping of
  // premium assets at the end of the first page.
  const totalAssets = premiumAssets.length + freeAssets.length;
  const desiredPremiumRatio = isFirstPage
    ? 0.5
    : premiumAssets.length / totalAssets;

  // Add a "chunk" size to vary the distribution
  const chunkSize = 4;
  // Show more free on the first page by avoiding blocks of premium assets
  const showPremiumBlocksAfter = isFirstPage ? 9 : 1;
  // Maximum number of consecutive premium assets allowed
  const maxSizePremiumBlock = 2;
  // Force first two assets on the first page to be free
  const freeAssetsAtStart = isFirstPage ? 2 : 1;
  let chunkPremiumCount = 0;
  let consecutivePremiumCount = 0;
  let remainingInChunk = chunkSize;
  let previousMaxSizePremiumBlock = false;

  /**
   * Main distribution loop:
   * - Processes assets in chunks of 4
   * - Uses hashValue for deterministic randomization
   * - Adjusts premium ratio based on current chunk distribution
   * - Enforces maximum consecutive premium assets rule
   */
  while (premiumIndex < premiumAssets.length || freeIndex < freeAssets.length) {
    const currentPosition = premiumIndex + freeIndex;
    const currentChunk = (currentPosition / chunkSize) | 0;
    const chunkPosition = currentPosition % chunkSize;

    remainingInChunk = chunkSize - chunkPosition;

    // Cache document ID lookup
    const currentPremiumId =
      premiumIndex < premiumAssets.length
        ? premiumAssets[premiumIndex]?.document?.id
        : '';

    // Hash the current chunk, position, premium count, and premium ID. This is used to determine the randomization.
    const hashValue = hashString(
      `${currentChunk}-${currentPosition}-${chunkPremiumCount}-${currentPremiumId}`
    );

    // Force premium assets if we need to meet minimum requirement
    const needMorePremiumInChunk = remainingInChunk + chunkPremiumCount < 2;

    // Adjust ratio based on how many premium items we've added in current chunk
    const adjustedRatio =
      desiredPremiumRatio *
      100 *
      (1 + (chunkPremiumCount / chunkSize - desiredPremiumRatio) * -0.5);

    const consecutivePremiumTolerance =
      currentPosition < showPremiumBlocksAfter ? 1 : maxSizePremiumBlock;

    let shouldAddPremium =
      // We always want to add a set number of free assets at the start
      currentPosition > freeAssetsAtStart - 1 &&
      // We don't want more than a maximum number of premium assets in a row
      consecutivePremiumCount < consecutivePremiumTolerance &&
      // We want to add a premium asset if we need to meet the minimum requirement for the chunk
      (needMorePremiumInChunk ||
        // We want to add a premium asset if the randomization says we should
        (hashValue % 100 < adjustedRatio &&
          premiumIndex < premiumAssets.length));

    // If we're about to create a block of premium tracks at the maximum size,
    // check if the last block of premium assets was also max size.
    // If it was, then force a free track and clear the max size flag
    const willCreateMaxSizePremiumBlock =
      shouldAddPremium && consecutivePremiumCount + 1 === maxSizePremiumBlock;

    if (willCreateMaxSizePremiumBlock && previousMaxSizePremiumBlock) {
      shouldAddPremium = false;
      previousMaxSizePremiumBlock = false;
    }

    // We want to add a premium asset if we've run out of free assets.
    // This should be the final override as if we can't add a free track, the
    // other checks are pointless.
    if (freeIndex >= freeAssets.length) {
      shouldAddPremium = true;
    }

    if (shouldAddPremium) {
      result[resultIndex] = premiumAssets[premiumIndex];
      resultIndex += 1;
      premiumIndex += 1;
      chunkPremiumCount += 1;
      consecutivePremiumCount += 1;
    } else {
      result[resultIndex] = freeAssets[freeIndex];
      resultIndex += 1;
      freeIndex += 1;
      // If the last premium block was max size
      if (consecutivePremiumCount === maxSizePremiumBlock) {
        previousMaxSizePremiumBlock = true;
      }
      consecutivePremiumCount = 0;
    }

    // Reset chunk premium counter at chunk boundaries
    if (chunkPosition === chunkSize - 1) {
      chunkPremiumCount = 0;
    }
  }

  return result.filter(Boolean);
};
